<!--
  PREVENTIVO › PICKUP & DELIVERY.

  Stessa impaginazione del preventivo valet: indirizzo del cliente e sede, poi
  una tabella con il pickup normale e quello con auto di cortesia, per una
  tratta e per andata e ritorno.

  Il prezzo è `cost` di `POST /pickup/range`, cioè la tariffa del PARTNER sulla
  distanza su strada casa del cliente → sede: quanto paga il cliente. Importi
  senza IVA.
-->
<template>
  <div class="ppk">
    <section class="ppk__card">
      <header class="ppk__head">
        <h2 class="ppk__title">
          Cliente e sede
        </h2>
        <p class="ppk__note">
          La tariffa dipende dalla distanza su strada fra la casa del cliente e la sede.
        </p>
      </header>

      <div class="ppk__body">
        <div class="ppk__label">
          Indirizzo del cliente
        </div>
        <div class="ppk__grid">
          <div class="ppk__f ppk__f--2">
            <label for="ppk-via">Via / Piazza</label>
            <el-input
              id="ppk-via"
              v-model="cliente.address"
            />
          </div>
          <div class="ppk__f">
            <label for="ppk-civico">Civico</label>
            <el-input
              id="ppk-civico"
              v-model="cliente.street_number"
            />
          </div>
          <div class="ppk__f">
            <label for="ppk-cap">CAP</label>
            <el-input
              id="ppk-cap"
              v-model="cliente.cap"
              maxlength="5"
            />
          </div>
          <div class="ppk__f">
            <label for="ppk-citta">Città</label>
            <el-input
              id="ppk-citta"
              v-model="cliente.city"
            />
          </div>
          <div class="ppk__f">
            <label for="ppk-provincia">Provincia</label>
            <el-input
              id="ppk-provincia"
              v-model="cliente.province"
              maxlength="2"
              placeholder="es. BG"
            />
          </div>
        </div>

        <label
          class="ppk__label"
          for="ppk-sede"
        >Sede</label>
        <el-select
          id="ppk-sede"
          v-model="sedeId"
          placeholder="Scegli la sede"
          class="ppk__sede"
          :disabled="!sedi.length"
        >
          <el-option
            v-for="s in sedi"
            :key="s.id"
            :label="etichettaSede(s)"
            :value="s.id"
          />
        </el-select>

        <div class="ppk__actions">
          <span
            v-if="tentato && problema"
            class="ppk__err"
            role="alert"
          >{{ problema }}</span>
          <span class="ppk__spacer" />
          <el-button
            type="primary"
            :loading="calcolo"
            @click="calcola"
          >
            Calcola preventivo
          </el-button>
        </div>
      </div>
    </section>

    <section
      v-if="risultato"
      class="ppk__card"
      aria-live="polite"
    >
      <header class="ppk__head">
        <h2 class="ppk__title">
          Preventivo
        </h2>
        <p class="ppk__note">
          {{ numero(risultato.km) }} km su strada · circa {{ durata(risultato.min) }}
        </p>
      </header>

      <div class="ppk__body">
        <div class="ppk__scroll">
          <table class="ppk__tab">
            <thead>
              <tr>
                <th>Servizio</th>
                <th class="num">
                  Una tratta
                </th>
                <th class="num">
                  Andata e ritorno
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in risultato.righe"
                :key="r.chiave"
              >
                <td class="ppk__opt">
                  {{ r.titolo }}
                </td>
                <td class="num ppk__strong">
                  {{ prezzo(r.tratta) }}
                </td>
                <td class="num">
                  {{ prezzo(r.tratta === null ? null : r.tratta * 2) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="ppk__help">
          Importi senza IVA, con le tariffe del partner in vigore oggi. Una tratta è
          solo l'andata o solo il ritorno.
          <template v-if="risultato.righe.some((r) => r.tratta === null)">
            Dove il prezzo manca, la tariffa del partner per questa distanza non è
            impostata: la imposta l'amministratore nella pagina Tariffe.
          </template>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import bookingAPI from '../services/bookingAPI.js';
import { messageForError } from '../services/httpErrors';
import { isLoggedStore } from '../store/logged';

const pulito = (v) => String(v ?? '').trim();
const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

// Le due varianti del listino pickup: si mostrano insieme, non si scelgono.
const VARIANTI = [
  { chiave: 'pickup', titolo: 'Pickup & Delivery', loaner: false },
  { chiave: 'pickup_loaner', titolo: 'Con auto di cortesia', loaner: true },
];

export default {
  name: 'PreventivoPickup',

  setup() {
    const store = isLoggedStore();
    const cliente = reactive({ address: '', street_number: '', cap: '', city: '', province: '' });
    const sedi = ref([]);
    const sedeId = ref('');
    const tentato = ref(false);
    const calcolo = ref(false);
    const risultato = ref(null);

    onMounted(async () => {
      try {
        const res = await bookingAPI.getHeadquartersByEmployee(store.id);
        sedi.value = res?.data?.[0]?.headquarters || [];
        if (sedi.value.length === 1) sedeId.value = sedi.value[0].id;
      } catch (e) {
        console.warn('Sedi non lette:', e?.message);
      }
    });

    const etichettaSede = (s) => [s.name, [s.address, s.street_number].filter(Boolean).join(' '), s.city]
      .filter(Boolean)
      .join(' · ');

    const problema = computed(() => {
      if (!pulito(cliente.address) || !pulito(cliente.city)) return 'Servono almeno via e città del cliente';
      if (!sedi.value.length) return 'Il tuo utente non ha sedi: il preventivo non si può calcolare';
      if (!sedeId.value) return 'Scegli la sede';
      return '';
    });

    const indirizzo = (a) => ({
      address: pulito(a.address),
      street_number: pulito(a.street_number),
      cap: pulito(a.cap),
      city: pulito(a.city),
      province: pulito(a.province).toUpperCase(),
    });

    const calcola = async () => {
      tentato.value = true;
      if (problema.value) return;
      calcolo.value = true;
      const sede = sedi.value.find((s) => s.id === sedeId.value);
      try {
        // Andata: casa del cliente → sede. Una richiesta per variante; se una
        // sola fallisce, l'altra si mostra comunque.
        const esiti = await Promise.allSettled(VARIANTI.map((v) => bookingAPI.getRangePickup({
          headquarter_id: sede.id,
          origins_point: indirizzo(cliente),
          destination_point: indirizzo(sede),
          loaner: v.loaner,
        })));
        const ok = esiti.find((e) => e.status === 'fulfilled');
        if (!ok) throw esiti[0].reason;
        risultato.value = {
          km: Number(ok.value.data?.km) || 0,
          min: Number(ok.value.data?.minute) || 0,
          righe: VARIANTI.map((v, i) => {
            const e = esiti[i];
            const costo = e.status === 'fulfilled' ? Number(e.value.data?.cost) : NaN;
            // Una fascia che manca vale 0 per il backend: qui è «non impostata».
            return { ...v, tratta: Number.isFinite(costo) && costo > 0 ? costo : null };
          }),
        };
      } catch (e) {
        console.error('Preventivo pickup non calcolato:', e);
        risultato.value = null;
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
      } finally {
        calcolo.value = false;
      }
    };

    const numero = (v) => (Math.round(Number(v) * 10) / 10).toLocaleString('it-IT');
    const durata = (min) => {
      const m = Math.round(Number(min) || 0);
      if (m < 60) return `${m} min`;
      return `${Math.floor(m / 60)} h ${m % 60} min`;
    };
    const prezzo = (v) => (v === null || v === undefined ? '—' : `${euroFmt.format(v)} + IVA`);

    return {
      cliente, sedi, sedeId, tentato, calcolo, risultato, problema, etichettaSede, calcola, numero, durata, prezzo,
    };
  },
};
</script>

<style scoped>
.ppk { display: flex; flex-direction: column; gap: 16px; text-align: left; }
.ppk__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.ppk__head { padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.ppk__title { margin: 0; font-size: 14.5px; font-weight: 600; }
.ppk__note { margin: 3px 0 0; font-size: 12.5px; color: var(--color-text-secondary); }
.ppk__body { padding: 16px 18px; display: flex; flex-direction: column; gap: 12px; }
.ppk__label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary);
}
.ppk__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 14px; }
.ppk__f { display: flex; flex-direction: column; min-width: 0; }
.ppk__f--2 { grid-column: span 2; }
.ppk__f label { margin-bottom: 4px; font-size: 12px; font-weight: 600; color: var(--color-text-secondary); }
.ppk__sede { max-width: 480px; }

.ppk__actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ppk__spacer { flex: 1 1 auto; }
.ppk__err { font-size: 12.5px; font-weight: 600; color: var(--state-attn); }
.ppk__help { margin: 0; font-size: 12px; line-height: 1.5; color: var(--color-text-muted); }

.ppk__scroll { overflow-x: auto; }
.ppk__tab { width: 100%; min-width: 420px; border-collapse: collapse; }
.ppk__tab th {
  text-align: left; padding: 8px; border-bottom: 1px solid var(--color-border);
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary);
}
.ppk__tab td { padding: 10px 8px; border-bottom: 1px solid var(--color-bg-tertiary); font-size: 13.5px; }
.ppk__tab tr:last-child td { border-bottom: 0; }
.ppk__tab .num { text-align: right; font-variant-numeric: tabular-nums; }
.ppk__opt { font-weight: 600; }
.ppk__strong { font-weight: 700; }

@media (max-width: 700px) {
  .ppk__grid { grid-template-columns: 1fr 1fr; }
}
</style>
